'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { Dropdown, Modal, Tooltip } from '@/shared/components';
import { useAppStore } from '@/store/app-store';
import { Address } from '@/types/user';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { getAddressState } from '@/app/utils';
import { CartSelectAddress, useCartStore } from '../cart-store';
import { DOMAIN } from '@/app/utils/constant';
import Link from 'next/link';
import { useCountry } from '@/hooks/use-country';
import { usePriceStore } from '@/store/price-store';
interface ModalInfo {
  show: boolean;
  callback: () => void;
}

export const Title = () => {
  const { shipToList = [], billToInfo } = useAppStore();
  const { address, changeAddress } = useCartStore();
  const { t } = useLang(NAMESPACE.cart);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { isKR } = useCountry();
  const { promotionInfo } = usePriceStore();
  const [popupInfo, setPopupInfo] = useState<ModalInfo>({ show: false, callback: () => {} });
  useEffect(() => {
    if (shipToList && shipToList.length > 0) {
      let defaultAddress = shipToList.find((ship) => ship.isDefault);
      if (!defaultAddress) {
        defaultAddress = shipToList[0];
      }
      onAddressChange(formatAddressValue(defaultAddress));
    }
  }, [shipToList]);

  const onAddressChange = (value: CartSelectAddress) => {
    changeAddress(value);
  };
  const onCancelModal = () => {
    setPopupInfo({ show: false, callback: () => {} });
  };
  const formatAddressValue = (unBrokenAddress: Address) => {
    return {
      addressKey: unBrokenAddress?.addressKey,
      accountNumber: unBrokenAddress?.accountNumber,
      provinceCode: unBrokenAddress?.provinceCode,
      categoryCode03: unBrokenAddress?.categoryCode03,
      isActive: getAddressState(unBrokenAddress) === 'active',
    };
  };

  const formatShipAddress = useMemo(() => {
    const {
      accountNumber: billToAccountNumber,
      accountName: billToAccountName,
      streetAddress: billToStreetAddress,
      buildingOrRoom: billToBuildingOrRoom,
      address3: billToAddress3,
      attentionTo: billToAttentionTo,
      city: billToCity,
    } = billToInfo || {};
    return shipToList?.map((item) => {
      const { accountNumber, streetAddress, accountName, buildingOrRoom, address3, attentionTo, city } = item;
      const pendingLabel = getAddressState(item) === 'pending' ? 'PENDING ' : ' ';
      const accountNumberLabel = accountNumber ? `${accountNumber} - ` : '';
      const accountNameLabel = isKR && accountName ? `${accountName} - ` : '';
      const streetAddressLabel = streetAddress ? `${streetAddress}` : '';
      return {
        label: pendingLabel + accountNumberLabel + accountNameLabel + streetAddressLabel,
        value: formatAddressValue(item),
        tooltip: (
          <div className="max-w-[400px]">
            <div className="my-2 popup-title">{streetAddress}</div>
            <div className="flex gap-4">
              <div className="flex-1">
                <div className="font-bold">{t('shipping.address')}</div>
                <div className="flex flex-col popup-style">
                  {accountName && <span>{accountName}</span>}
                  {streetAddress && <span>{streetAddress}</span>}
                  {(buildingOrRoom || address3 || attentionTo) && (
                    <span>
                      {buildingOrRoom}
                      {buildingOrRoom && address3 ? ', ' : ''}
                      {address3}
                      {buildingOrRoom || address3 ? ', ' : ''}
                      {attentionTo}
                    </span>
                  )}
                  {city && <span>{city}</span>}
                </div>
                <div className="flex flex-col mt-4 justify-end flex-1">
                  <span className="font-bold">{t('account.number')}</span>
                  <span>{accountNumber}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="font-bold">{t('billing.address')}</div>
                <div className="flex flex-col popup-style">
                  {billToAccountName && <span>{billToAccountName}</span>}
                  {billToStreetAddress && <span>{billToStreetAddress}</span>}
                  {(billToBuildingOrRoom || billToAddress3 || billToAttentionTo) && (
                    <span>
                      {billToBuildingOrRoom}
                      {billToBuildingOrRoom && billToAddress3 ? ', ' : ''}
                      {billToAddress3}
                      {billToBuildingOrRoom || billToAddress3 ? ', ' : ''}
                      {billToAttentionTo}
                    </span>
                  )}
                  {billToCity && <span>{billToCity}</span>}
                </div>
                <div className="flex flex-col mt-4 justify-end flex-1">
                  <span className="font-bold">{t('account.number')}</span>
                  <span>{billToAccountNumber}</span>
                </div>
              </div>
            </div>
          </div>
        ),
      };
    });
  }, [shipToList]);
  return (
    <div className="col-span-1 flex mt-8 w-full print:hidden mb-5 lg:mb-0 md:mb-0 sm:mb-0 sm:flex-col sm:gap-y-2">
      <div className="flex items-baseline gap-x-4 flex-1 w-0 min-w-[150px]">
        <h1 className="font-medium">{t('cart')}</h1>
        <span className="text-gray-1100 text-xs font-medium">{t('auto.saved')}</span>
      </div>
      {shipToList && shipToList.length > 0 && (
        <div className="grid 2xl:grid-cols-[1fr_342px_1fr] grid-cols-[1fr_342px] sm:grid-cols-1 gap-x-4">
          <span className="justify-self-end sm:justify-self-start sm:mb-1 flex items-center text-gray-1100 text-sm font-bold">
            {t('switch.shipping.address')}
          </span>
          <Dropdown
            className="w-full self-center sm:row-start-2"
            size="small"
            customOnSelect={(e) => {
              if (promotionInfo?.promoCode) {
                setPopupInfo({
                  show: true,
                  callback: () => {
                    onAddressChange(e);
                  },
                });
              } else {
                onAddressChange(e);
              }
            }}
            value={formatShipAddress.find((option) => JSON.stringify(option.value) === JSON.stringify(address))?.value}
            options={formatShipAddress}
            showToolTip={true}
          />
          <Link
            prefetch
            href={`${DOMAIN}/store/v2/account-center/my-account/profile/shipping-and-billing/edit-shipping-address/${address?.addressKey}`}
            className="p-0 text-left 2xl:col-start-3 2xl:row-start-1 col-start-2 row-start-2 sm:row-start-3 sm:col-start-1 flex items-center"
          >
            <span className="text-sm font-normal truncate">{t('edit.address.or.request.new.address')}</span>
          </Link>
        </div>
      )}
      <Modal
        title={<span className="sm:text-xl"></span>}
        size="small"
        isOpen={popupInfo?.show}
        handleClose={onCancelModal}
        handleCancel={onCancelModal}
        handleSave={() => {
          popupInfo?.callback();
          onCancelModal();
        }}
        saveButtonLabel={commonT('common.yes')}
        cancelButtonLabel={commonT('common.no')}
      >
        <div className="flex mt-2 sm:flex-col sm:flex-1 sm:px-1 sm:mt-0 ">{t('address.change')}</div>
      </Modal>
    </div>
  );
};
